import { useContext, useState } from "react";
import { Button, Card, Form, Input, Tooltip, Typography, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faPencil } from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../context/AuthProvider";
import { authService } from "../../services/authService";
import ChangePasswordModal from "./ChangePasswordModal";
import styles from "./settings.module.scss";

const { Title, Text } = Typography;

const ProfileTab: React.FC = () => {
  const { user, setUserData } = useContext(AuthContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const handleEdit = () => {
    form.setFieldsValue({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const updated = await authService.updateProfile(values, user?.id ?? '');
      setUserData(updated);
      setIsEditing(false);
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || err.message || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card
        className={`${styles.card} ${styles.profileCard}`}
        title={
          <div className={styles.cardTitleWrapper}>
            <Title level={5} className={styles.cardTitleText}>Personal Info</Title>
            <Text type="secondary" className={styles.cardSubtitle}>
              Name and email associated to this XD API account
            </Text>
          </div>
        }
        extra={
          !isEditing && (
            <Tooltip title="Edit Details">
              <FontAwesomeIcon icon={faPencil} className={styles.editIcon} onClick={handleEdit} />
            </Tooltip>
          )
        }
        actions={
          isEditing
            ? [
                <div className={styles.cardActions}>
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button type="primary" loading={saving} onClick={handleSave}>Save Changes</Button>
                </div>
              ]
            : [
                <div className={styles.changePasswordAction}>
                  <Button type="link" onClick={() => setModalOpen(true)}>
                    Change Password
                    <FontAwesomeIcon icon={faChevronRight} className={styles.changePasswordChevron} />
                  </Button>
                </div>
              ]
        }
      >
        <Form form={form}>
          <div className={styles.profileFields}>
            <div className={styles.fieldColumn}>
              <Text type="secondary" className={styles.fieldLabel}>First Name</Text>
              {isEditing ? (
                <Form.Item name="firstName" style={{ margin: 0 }} rules={[{ required: true, message: "" }]}>
                  <Input className={styles.profileInput} />
                </Form.Item>
              ) : (
                <Text>{user?.firstName || "—"}</Text>
              )}
            </div>
            <div className={styles.fieldColumn}>
              <Text type="secondary" className={styles.fieldLabel}>Last Name</Text>
              {isEditing ? (
                <Form.Item name="lastName" style={{ margin: 0 }} rules={[{ required: true, message: "" }]}>
                  <Input className={styles.profileInput} />
                </Form.Item>
              ) : (
                <Text>{user?.lastName || "—"}</Text>
              )}
            </div>
          </div>
          <div className={styles.profileFieldsRow}>
            <div className={styles.fieldColumn}>
              <Text type="secondary" className={styles.fieldLabel}>Email</Text>
              {isEditing ? (
                <Form.Item name="email" style={{ margin: 0 }} rules={[{ required: true, type: "email", message: "" }]}>
                  <Input className={styles.profileInput} />
                </Form.Item>
              ) : (
                <Text>{user?.email || "—"}</Text>
              )}
            </div>
          </div>
        </Form>
      </Card>

      <ChangePasswordModal open={modalOpen} onClose={() => setModalOpen(false)} user={user}/>
    </>
  );
};

export default ProfileTab;
